import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRefreshQuote } from "../../hooks/investments";
import { mapInvestmentsError } from "../../lib/investments-errors";

interface Props {
  investmentId: string;
  disabled?: boolean;
  disabledReason?: string;
}

export function RefreshButton({ investmentId, disabled, disabledReason }: Props) {
  const { t } = useTranslation();
  const refresh = useRefreshQuote();
  const [feedback, setFeedback] = useState<{ kind: "ok" | "err"; message: string } | null>(null);

  async function handleClick() {
    setFeedback(null);
    try {
      const quote = await refresh.mutateAsync(investmentId);
      setFeedback({
        kind: "ok",
        message: t("investments.refresh.success", {
          price: (quote.priceCents / 100).toLocaleString("de-DE", {
            style: "currency",
            currency: quote.currency,
          }),
        }),
      });
    } catch (err) {
      const code =
        err &&
        typeof err === "object" &&
        "code" in err &&
        typeof (err as { code: unknown }).code === "string"
          ? (err as { code: string }).code
          : undefined;
      setFeedback({
        kind: "err",
        message: code ? t(mapInvestmentsError(code)) : t("errors.unknown"),
      });
    }
  }

  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || refresh.isPending}
        title={disabled ? disabledReason : undefined}
        className="px-3 py-1 border rounded disabled:opacity-50 inline-flex items-center gap-1"
      >
        {refresh.isPending ? <span className="animate-spin">⟳</span> : <span>↻</span>}
        {t("investments.refresh.button")}
      </button>
      {feedback && (
        <span className={`text-sm ${feedback.kind === "ok" ? "text-green-700" : "text-red-700"}`}>
          {feedback.message}
        </span>
      )}
    </div>
  );
}
